import { csv, DEFAULT_ENDPOINT, intAttr, warn } from "../core/config.js";
import type { ListConfig } from "../core/types.js";

const DEFAULT_SEARCH_PAGE_SIZE = 20;
const DEFAULT_MIN_LENGTH = 2;
/** The server rejects `q` longer than 100 chars; truncate rather than 400. */
export const MAX_QUERY_LENGTH = 100;

/**
 * Resolves one search block's config, with the same precedence as lists:
 * attributes on the wrapper win over `window.SlaterSearchConfig.search[key]`,
 * which wins over defaults. Search has no collection, locale, sort, facets or
 * base filter — `GET /:siteId/search` takes only q/limit/offset/expand.
 */
export function resolveSearchConfig(wrapper: Element, key: string): ListConfig | null {
  const global = window.SlaterSearchConfig ?? {};
  const per = global.search?.[key] ?? {};

  const siteId =
    wrapper.getAttribute("data-slater-site") ??
    per.siteId ??
    global.siteId ??
    document.documentElement.getAttribute("data-wf-site") ??
    "";

  if (!siteId) {
    warn(
      `search "${key}": no site id. Set data-slater-site, SlaterSearchConfig.siteId, ` +
        `or publish from Webflow (which stamps data-wf-site on <html>).`,
    );
    return null;
  }

  const pageAttr = wrapper.getAttribute("data-slater-page-size");
  const initialLimit = pageAttr
    ? intAttr(wrapper, "data-slater-page-size", DEFAULT_SEARCH_PAGE_SIZE)
    : per.initialLimit ?? DEFAULT_SEARCH_PAGE_SIZE;

  const loadMoreAmount =
    intAttr(wrapper, "data-slater-load-more-amount", 0) ||
    per.loadMoreAmount ||
    initialLimit;

  const minAttr = wrapper.getAttribute("data-slater-min-length");
  const minLength = minAttr
    ? intAttr(wrapper, "data-slater-min-length", DEFAULT_MIN_LENGTH)
    : per.minLength ?? DEFAULT_MIN_LENGTH;

  const expandAttr = wrapper.getAttribute("data-slater-expand");

  if (wrapper.hasAttribute("data-slater-search-fields")) {
    warn(
      `search "${key}": data-slater-search-fields is ignored on a global search ` +
        `block — it spans collections that share no field schema. Scope a ` +
        `per-collection list instead.`,
    );
  }

  return {
    key,
    mode: "search",
    endpoint: (per.endpoint ?? global.endpoint ?? DEFAULT_ENDPOINT).replace(/\/+$/, ""),
    siteId,
    collection: "search", // unused — the datasource builds /:siteId/search itself
    group: undefined,
    locale: undefined,
    initialLimit,
    loadMoreAmount,
    minLength,
    sort: undefined,
    detailPath: undefined,
    facets: [],
    expand:
      expandAttr === null
        ? per.expand ?? []
        : csv(expandAttr).length > 0
          ? csv(expandAttr)
          : ["*"],
    // Site-wide search takes no field scoping — warned about above.
    searchFields: [],
    baseFilter: {},
  };
}
